import { useContext, useEffect } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirmationModal } from "@/context/confirmationModalContext";

export function useAssignmentLeaveGuard(dirty: boolean) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const message = t("planner.leave_warning");
  useEffect(() => {
    if (!dirty) return;
    let restoring = false;
    let approvedGoIndex: number | undefined;
    let index: number | undefined = window.history.state?.idx;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const pop = (event: PopStateEvent) => {
      const nextIndex: number | undefined = event.state?.idx;
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (approvedGoIndex !== undefined && approvedGoIndex === nextIndex) {
        approvedGoIndex = undefined;
        index = nextIndex;
        return;
      }
      approvedGoIndex = undefined;
      if (window.confirm(message)) {
        index = nextIndex;
        return;
      }
      if (
        typeof index === "number" &&
        typeof nextIndex === "number" &&
        index !== nextIndex
      ) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - nextIndex);
      }
    };
    const push = navigator.push;
    const replace = navigator.replace;
    const go = navigator.go;
    navigator.push = async (...args: Parameters<typeof push>) => {
      if (await showConfirmationModalAsync(message))
        push.apply(navigator, args);
    };
    navigator.replace = async (...args: Parameters<typeof replace>) => {
      if (await showConfirmationModalAsync(message))
        replace.apply(navigator, args);
    };
    navigator.go = async (...args: Parameters<typeof go>) => {
      if (await showConfirmationModalAsync(message)) {
        const currentIndex: number | undefined = window.history.state?.idx;
        approvedGoIndex =
          typeof currentIndex === "number" ? currentIndex + args[0] : undefined;
        go.apply(navigator, args);
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    window.addEventListener("popstate", pop, true); // Capture before BrowserRouter can unmount the in-memory plan.
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener("beforeunload", beforeUnload);
      window.removeEventListener("popstate", pop, true);
    };
  }, [dirty, navigator, message, showConfirmationModalAsync]);
}
